import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AdminPagination } from '@/components/AdminPagination';
import { AdminPill } from '@/components/AdminPill';
import { AdminTableSkeletonRows } from '@/components/AdminTableSkeletonRows';
import { AdminTableToolbar } from '@/components/AdminTableToolbar';
import { useAdminTablePagination } from '@/hooks/useAdminTablePagination';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useAdminUsers, type AdminUsersFilter } from './AdminUsersPage.data';
import type {
  AdminUserRoleFilter,
  AdminUserRow,
  AdminUserStatusFilter,
} from '@/lib/db/admin/users';
import { promoteUser, disableUser, reactivateUser } from '@/lib/db/admin/users';
import { formatGstDate } from '@/lib/format';
import { parsePageParam, parsePageSizeParam } from '@/lib/pagination';

const FILTERS = ['all', 'buyers', 'admins', 'superAdmins', 'disabled'] as const;
type FilterKey = (typeof FILTERS)[number];

function filterToQuery(filter: FilterKey): {
  role: AdminUserRoleFilter;
  status: AdminUserStatusFilter;
} {
  switch (filter) {
    case 'buyers':
      return { role: 'buyer', status: 'all' };
    case 'admins':
      return { role: 'admin', status: 'all' };
    case 'superAdmins':
      return { role: 'super_admin', status: 'all' };
    case 'disabled':
      return { role: 'all', status: 'disabled' };
    case 'all':
    default:
      return { role: 'all', status: 'all' };
  }
}

function roleLabel(role: 'buyer' | 'admin' | 'super_admin' | string): string {
  if (role === 'super_admin') return 'Super Admin';
  if (role === 'admin') return 'Admin';
  return 'Buyer';
}

type ActionMenuItem = {
  key: string;
  labelKey: string;
  action: () => Promise<void>;
  danger?: boolean;
};

function useUserActions(
  user: AdminUserRow,
  onUpdated: (updated: AdminUserRow) => void,
  t: (key: string, opts?: Record<string, string>) => string,
): ActionMenuItem[] {
  const items: ActionMenuItem[] = [];
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email;
  const isActive = user.status === 'active' && user.deleted_at === null;

  if (user.is_root) return [];

  if (isActive) {
    if (user.role === 'buyer') {
      items.push({
        key: 'promote-admin',
        labelKey: 'admin.users.actions.promoteToAdmin',
        action: async () => {
          if (
            !window.confirm(
              t('admin.users.actions.confirmPromote', { name: fullName, role: 'Admin' }),
            )
          )
            return;
          const updated = await promoteUser(user.id, 'admin');
          toast.success(t('admin.users.actions.promoted'));
          onUpdated(updated);
        },
      });
    }
    if (user.role === 'admin') {
      items.push({
        key: 'promote-super',
        labelKey: 'admin.users.actions.promoteToSuperAdmin',
        action: async () => {
          if (
            !window.confirm(
              t('admin.users.actions.confirmPromote', { name: fullName, role: 'Super Admin' }),
            )
          )
            return;
          const updated = await promoteUser(user.id, 'super_admin');
          toast.success(t('admin.users.actions.promoted'));
          onUpdated(updated);
        },
      });
      items.push({
        key: 'demote-buyer',
        labelKey: 'admin.users.actions.demoteToBuyer',
        action: async () => {
          if (
            !window.confirm(
              t('admin.users.actions.confirmDemote', { name: fullName, role: 'Buyer' }),
            )
          )
            return;
          const updated = await promoteUser(user.id, 'buyer');
          toast.success(t('admin.users.actions.demoted'));
          onUpdated(updated);
        },
      });
    }
    if (user.role === 'super_admin') {
      items.push({
        key: 'demote-admin',
        labelKey: 'admin.users.actions.demoteToAdmin',
        action: async () => {
          if (
            !window.confirm(
              t('admin.users.actions.confirmDemote', { name: fullName, role: 'Admin' }),
            )
          )
            return;
          const updated = await promoteUser(user.id, 'admin');
          toast.success(t('admin.users.actions.demoted'));
          onUpdated(updated);
        },
      });
    }
    items.push({
      key: 'disable',
      labelKey: 'admin.users.actions.disable',
      danger: true,
      action: async () => {
        if (!window.confirm(t('admin.users.actions.confirmDisable', { name: fullName }))) return;
        const updated = await disableUser(user.id);
        toast.success(t('admin.users.actions.disabled'));
        onUpdated(updated);
      },
    });
  } else if (user.status === 'disabled') {
    items.push({
      key: 'reactivate',
      labelKey: 'admin.users.actions.reactivate',
      action: async () => {
        if (!window.confirm(t('admin.users.actions.confirmReactivate', { name: fullName }))) return;
        const updated = await reactivateUser(user.id);
        toast.success(t('admin.users.actions.reactivated'));
        onUpdated(updated);
      },
    });
  }

  return items;
}

function UserActionsMenu({
  user,
  onUpdated,
}: {
  user: AdminUserRow;
  onUpdated: (updated: AdminUserRow) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const items = useUserActions(user, onUpdated, t);

  const handleAction = useCallback(
    async (action: () => Promise<void>) => {
      setBusy(true);
      try {
        await action();
      } catch (err) {
        const msg =
          err instanceof Error && err.message.includes('R7')
            ? err.message
            : t('admin.users.actions.error');
        toast.error(msg);
      } finally {
        setBusy(false);
        setOpen(false);
      }
    },
    [t],
  );

  if (user.is_root) {
    return (
      <span
        style={{ fontSize: '0.75rem', color: 'var(--c-muted)' }}
        title={t('admin.users.actions.rootProtected')}
      >
        —
      </span>
    );
  }

  if (items.length === 0) return null;

  return (
    <div style={{ position: 'relative' }} ref={menuRef}>
      <button
        type="button"
        className="admin-cell-link"
        aria-label="More actions"
        aria-expanded={open}
        disabled={busy}
        onClick={() => setOpen((v) => !v)}
      >
        ⋯
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 90 }} onClick={() => setOpen(false)} />
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: '100%',
              zIndex: 100,
              background: 'var(--c-surface, #fff)',
              border: '1px solid var(--c-border, #e5e7eb)',
              borderRadius: '0.5rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              minWidth: '200px',
              padding: '0.25rem 0',
            }}
          >
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                disabled={busy}
                onClick={() => handleAction(item.action)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'start',
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: item.danger ? 'var(--c-danger, #dc2626)' : 'inherit',
                }}
              >
                {t(item.labelKey)}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function AdminUsersPage() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<FilterKey>('all');
  const [version, setVersion] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  // URL is the pagination source of truth (read every render — Back/Forward
  // replays states natively). Writes go through useAdminTablePagination.
  const [searchParams] = useSearchParams();
  const page = parsePageParam(searchParams.get('page'));
  const pageSize = parsePageSizeParam(searchParams.get('pageSize'));
  const debouncedSearch = useDebouncedValue(searchQuery);

  const queryFilter: AdminUsersFilter = {
    ...filterToQuery(filter),
    searchQuery: debouncedSearch,
    page,
    pageSize,
  };
  const { rows, total, status } = useAdminUsers(queryFilter, version);

  const { goPage, setPageSize } = useAdminTablePagination({
    page,
    pageSize,
    total,
    isSuccess: status === 'success',
    filterKey: `${filter}|${debouncedSearch}`,
  });

  const handleUserUpdated = useCallback(() => {
    setVersion((v) => v + 1);
  }, []);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirst, setInviteFirst] = useState('');
  const [inviteLast, setInviteLast] = useState('');
  const [inviting, setInviting] = useState(false);

  const handleInvite = async () => {
    if (!inviteEmail.trim() || !inviteFirst.trim() || !inviteLast.trim()) return;
    setInviting(true);
    try {
      const { getSupabaseClient } = await import('@/lib/supabase');
      const client = getSupabaseClient();
      const { error } = await client.functions.invoke('invite-admin', {
        body: {
          email: inviteEmail.trim(),
          first_name: inviteFirst.trim(),
          last_name: inviteLast.trim(),
        },
      });
      if (error) throw error;
      toast.success(t('admin.users.invitedToast'));
      setInviteOpen(false);
      setInviteEmail('');
      setInviteFirst('');
      setInviteLast('');
      setVersion((v) => v + 1);
    } catch (err) {
      // supabase-js FunctionsHttpError carries the Edge Response in `context`.
      // Map the HTTP status to a clear message — 409 means the email already
      // has an account (use Promote instead of Invite).
      const status = (err as { context?: { status?: number } })?.context?.status;
      const keyByStatus: Record<number, string> = {
        409: 'admin.users.inviteError.emailExists',
        403: 'admin.users.inviteError.forbidden',
        400: 'admin.users.inviteError.invalid',
      };
      toast.error(t(keyByStatus[status ?? 0] ?? 'admin.users.inviteError.generic'));
    } finally {
      setInviting(false);
    }
  };

  return (
    <>
      <header className="admin-head">
        <div>
          <h1 className="admin-head__title">{t('admin.users.title')}</h1>
          <div className="admin-head__sub">{t('admin.users.sub', { count: total })}</div>
        </div>
        <div className="admin-head__actions">
          <button type="button" className="admin-btn" onClick={() => setInviteOpen(true)}>
            {t('admin.users.invite')}
          </button>
        </div>
      </header>

      <AdminTableToolbar
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder={t('admin.common.searchPlaceholder')}
      >
        {FILTERS.map((key) => (
          <button
            key={key}
            type="button"
            className={`admin-filter-tab${filter === key ? ' is-active' : ''}`}
            onClick={() => setFilter(key)}
          >
            {t(`admin.users.filters.${key}`)}
          </button>
        ))}
      </AdminTableToolbar>

      <div className="admin-table-wrap" aria-busy={status === 'loading'}>
        <table className="admin-table admin-table--sticky-actions">
          <thead>
            <tr>
              <th>{t('admin.users.headers.name')}</th>
              <th>{t('admin.users.headers.email')}</th>
              <th>{t('admin.users.headers.role')}</th>
              <th>{t('admin.users.headers.signedUp')}</th>
              <th>{t('admin.users.headers.lastLogin')}</th>
              <th>{t('admin.users.headers.status')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {status === 'loading' ? (
              <AdminTableSkeletonRows columns={7} />
            ) : (
              rows.map((user) => {
                const fullName =
                  [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email;
                const isActive = user.status === 'active' && user.deleted_at === null;
                const pillStatus = isActive ? 'ACTIVE' : 'ARCHIVED';
                const pillKey = isActive ? 'admin.common.activePill' : 'admin.common.archivedPill';
                return (
                  <tr key={user.id}>
                    <td>
                      <span className="admin-cell-ellipsis" title={fullName}>
                        {fullName}
                      </span>
                    </td>
                    <td>
                      <span className="admin-cell-ellipsis" title={user.email}>
                        {user.email}
                      </span>
                    </td>
                    <td
                      style={{
                        color: user.role === 'super_admin' ? 'var(--ddm-bronze)' : undefined,
                      }}
                    >
                      {roleLabel(user.role)}
                    </td>
                    <td className="admin-cell--mono">{formatGstDate(user.created_at)}</td>
                    <td className="admin-cell--mono">
                      {user.last_sign_in_at ? formatGstDate(user.last_sign_in_at) : '—'}
                    </td>
                    <td>
                      <AdminPill status={pillStatus} label={t(pillKey)} />
                    </td>
                    <td>
                      <UserActionsMenu user={user} onUpdated={handleUserUpdated} />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <AdminPagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={goPage}
        onPageSizeChange={setPageSize}
        idPrefix="admin-users"
      />

      {inviteOpen && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200 }}
            onClick={() => setInviteOpen(false)}
          />
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 201,
              background: 'var(--c-surface, #fff)',
              borderRadius: '0.75rem',
              padding: '2rem',
              width: '100%',
              maxWidth: '420px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            }}
          >
            <h3 style={{ margin: '0 0 1rem', fontWeight: 700 }}>Invite new admin</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <input
                type="email"
                className="m5-field__input"
                placeholder="Email address"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  className="m5-field__input"
                  placeholder="First name"
                  value={inviteFirst}
                  onChange={(e) => setInviteFirst(e.target.value)}
                />
                <input
                  type="text"
                  className="m5-field__input"
                  placeholder="Last name"
                  value={inviteLast}
                  onChange={(e) => setInviteLast(e.target.value)}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="admin-btn"
                  onClick={() => setInviteOpen(false)}
                  disabled={inviting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="admin-btn admin-btn--primary"
                  onClick={handleInvite}
                  disabled={
                    inviting || !inviteEmail.trim() || !inviteFirst.trim() || !inviteLast.trim()
                  }
                >
                  {inviting ? 'Sending…' : 'Send invite'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
